// The rule builder's model: the common rule shapes as a form, and the QQL the
// form stands for. A rule built here is written as ordinary WHEN/THEN text and
// saved through the same endpoint as a rule typed by hand, so the server's
// compiler is the one judge of it. Reading a stored rule back into the form is
// exact or not at all: the text is parsed into the model, the model is written
// out again, and the rule opens in the builder only when the two say the same
// thing (variable names aside). Anything else stays in the QQL editor.

export const TYPE_PREDICATE = ':type';

export type CompareOp = 'eq' | 'ne' | 'gt' | 'ge' | 'lt' | 'le';
export type LiteralKind = 'number' | 'date' | 'text';

/** What a condition asks of one property's value. */
export type Test =
  | { kind: 'is'; term: string }
  | { kind: 'anyOf'; valueKind: 'thing' | 'number' | 'text'; values: string[] }
  | { kind: 'flag'; value: boolean }
  | { kind: 'compare'; op: CompareOp; valueKind: LiteralKind; value: string }
  | { kind: 'compareProperty'; op: CompareOp; property: string }
  | { kind: 'contains'; text: string }
  | { kind: 'exists' };

export interface Condition {
  /** Whose property: the thing the rule is about, or the thing it links to. */
  on: 'subject' | 'linked';
  property: string;
  test: Test;
}

export type Outcome =
  | { kind: 'select' }
  | { kind: 'type'; cls: string }
  | { kind: 'link'; property: string; term: string }
  | { kind: 'value'; property: string; valueKind: 'number' | 'text' | 'boolean'; value: string };

export interface BuilderRule {
  subjectClass: string;
  /** One hop to another thing: "a Case has a case line that is a Case Line". */
  link: { property: string; cls: string | null } | null;
  conditions: Condition[];
  /** Exceptions: the rule does not hold for a thing that meets any of these. */
  unless: Condition[];
  outcome: Outcome;
}

export function blankBuilderRule(): BuilderRule {
  return { subjectClass: '', link: null, conditions: [], unless: [], outcome: { kind: 'select' } };
}

// ---- terms and literals ----

const NAME = /^[\p{L}\p{N}_-]*:[\p{L}\p{N}_-]+$/u;
const SCHEMES = new Set(['http', 'https', 'urn', 'file', 'mailto', 'tag']);

/** A stored term as QQL writes it: a prefixed name as it is, anything else
 *  (a full address) in angle brackets. */
export function qqlTerm(term: string): string {
  const prefix = term.slice(0, term.indexOf(':'));
  return NAME.test(term) && !SCHEMES.has(prefix.toLowerCase()) ? term : `<${term}>`;
}

const NUMBER = /^-?\d+(\.\d+)?$/;
const DATE = /^\d{4}-\d{2}-\d{2}$/;

function quote(text: string): string {
  return `"${text.replace(/\\/g, '\\\\').replace(/"/g, '\\"')}"`;
}

function literal(kind: LiteralKind | 'boolean', value: string): string {
  if (kind === 'number' || kind === 'boolean') return value.trim();
  return quote(kind === 'date' ? value.trim() : value);
}

const OP_SYMBOL: Record<CompareOp, string> = { eq: '=', ne: '!=', gt: '>', ge: '>=', lt: '<', le: '<=' };
const SYMBOL_OP: Record<string, CompareOp> = { '=': 'eq', '!=': 'ne', '>': 'gt', '>=': 'ge', '<': 'lt', '<=': 'le' };

// ---- what is missing ----

/** What the form still needs before it is a rule, in the words the form uses.
 *  Empty when the rule can be written. */
export function builderProblems(rule: BuilderRule): string[] {
  const out: string[] = [];
  if (!rule.subjectClass) out.push('Pick the kind of thing the rule is about.');
  if (rule.link && !rule.link.property) out.push('Pick the link to the other thing.');
  const check = (c: Condition, where: string) => {
    if (c.on === 'linked' && !rule.link) {
      out.push(`${where}: it refers to a linked thing, but the rule has no link.`);
      return;
    }
    if (!c.property) {
      out.push(`${where}: pick a property.`);
      return;
    }
    const t = c.test;
    if (t.kind === 'is' && !t.term) out.push(`${where}: pick the value.`);
    if (t.kind === 'anyOf') {
      if (t.values.length === 0) out.push(`${where}: add at least one value.`);
      if (t.valueKind === 'number' && t.values.some((v) => !NUMBER.test(v.trim()))) {
        out.push(`${where}: every value must be a number.`);
      }
    }
    if (t.kind === 'compare') {
      if (!t.value.trim()) out.push(`${where}: enter the value to compare with.`);
      else if (t.valueKind === 'number' && !NUMBER.test(t.value.trim())) {
        out.push(`${where}: “${t.value}” is not a number.`);
      } else if (t.valueKind === 'date' && !DATE.test(t.value.trim())) {
        out.push(`${where}: enter the date as year-month-day.`);
      }
    }
    if (t.kind === 'compareProperty' && !t.property) out.push(`${where}: pick the property to compare with.`);
    if (t.kind === 'contains' && !t.text.trim()) out.push(`${where}: enter the text to look for.`);
  };
  rule.conditions.forEach((c, i) => check(c, `Condition ${i + 1}`));
  rule.unless.forEach((c, i) => check(c, `Exception ${i + 1}`));
  const o = rule.outcome;
  if (o.kind === 'type' && !o.cls) out.push('Then: pick the kind of thing it becomes.');
  if (o.kind === 'link' && (!o.property || !o.term)) out.push('Then: pick the property and the value it gets.');
  if (o.kind === 'value') {
    if (!o.property) out.push('Then: pick the property it gets.');
    if (!o.value.trim()) out.push('Then: enter the value it gets.');
    else if (o.valueKind === 'number' && !NUMBER.test(o.value.trim())) out.push(`Then: “${o.value}” is not a number.`);
  }
  if (o.kind === 'select' && rule.conditions.length === 0 && rule.unless.length === 0 && !rule.link) {
    out.push('A rule that only picks things out needs at least one condition.');
  }
  return out;
}

// ---- writing QQL ----

function localOf(term: string): string {
  const idx = Math.max(term.lastIndexOf('#'), term.lastIndexOf('/'), term.lastIndexOf(':'));
  return idx >= 0 ? term.slice(idx + 1) : term;
}

/** A variable name from a term: `:CaseLine` → `caseLine`. */
function varBase(term: string, fallback: string): string {
  const words = localOf(term).split(/[^A-Za-z0-9]+/).filter(Boolean);
  if (words.length === 0) return fallback;
  const joined = words
    .map((w, i) => (i === 0 ? w.charAt(0).toLowerCase() + w.slice(1) : w.charAt(0).toUpperCase() + w.slice(1)))
    .join('');
  return /^[A-Za-z]/.test(joined) ? joined : `${fallback}${joined}`;
}

class Vars {
  private used = new Set<string>();
  take(term: string, fallback: string): string {
    const base = varBase(term, fallback);
    let name = base;
    for (let n = 2; this.used.has(name); n++) name = `${base}${n}`;
    this.used.add(name);
    return `?${name}`;
  }
}

function conditionLines(c: Condition, subject: string, linked: string | null, vars: Vars): string[] {
  const owner = c.on === 'linked' && linked ? linked : subject;
  const p = qqlTerm(c.property);
  const t = c.test;
  switch (t.kind) {
    case 'is':
      return [`${owner} ${p} ${qqlTerm(t.term)} .`];
    case 'flag':
      return [`${owner} ${p} ${t.value ? 'true' : 'false'} .`];
    case 'exists':
      return [`${owner} ${p} ${vars.take(c.property, 'value')} .`];
    case 'compare': {
      const v = vars.take(c.property, 'value');
      return [`${owner} ${p} ${v} .`, `FILTER (${v} ${OP_SYMBOL[t.op]} ${literal(t.valueKind, t.value)})`];
    }
    case 'anyOf': {
      const v = vars.take(c.property, 'value');
      const list = t.values
        .map((x) => (t.valueKind === 'thing' ? qqlTerm(x) : literal(t.valueKind, x)))
        .join(', ');
      return [`${owner} ${p} ${v} .`, `FILTER (${v} IN (${list}))`];
    }
    case 'contains': {
      const v = vars.take(c.property, 'value');
      return [`${owner} ${p} ${v} .`, `FILTER (CONTAINS(LCASE(${v}), ${quote(t.text.toLowerCase())}))`];
    }
    case 'compareProperty': {
      const v = vars.take(c.property, 'value');
      const w = vars.take(t.property, 'other');
      return [
        `${owner} ${p} ${v} .`,
        `${owner} ${qqlTerm(t.property)} ${w} .`,
        `FILTER (${v} ${OP_SYMBOL[t.op]} ${w})`,
      ];
    }
  }
}

/** The WHEN and THEN the form stands for. Call only when `builderProblems` is
 *  empty: an unfinished form writes unfinished text. */
export function generateRule(rule: BuilderRule): { when: string; then: string } {
  const vars = new Vars();
  const subject = vars.take(rule.subjectClass, 'thing');
  const lines = [`${subject} ${TYPE_PREDICATE} ${qqlTerm(rule.subjectClass)} .`];
  let linked: string | null = null;
  if (rule.link) {
    linked = vars.take(rule.link.cls ?? rule.link.property, 'linked');
    lines.push(`${subject} ${qqlTerm(rule.link.property)} ${linked} .`);
    if (rule.link.cls) lines.push(`${linked} ${TYPE_PREDICATE} ${qqlTerm(rule.link.cls)} .`);
  }
  for (const c of rule.conditions) lines.push(...conditionLines(c, subject, linked, vars));
  for (const c of rule.unless) {
    lines.push(`FILTER NOT EXISTS { ${conditionLines(c, subject, linked, vars).join(' ')} }`);
  }
  const o = rule.outcome;
  const then =
    o.kind === 'select'
      ? ''
      : o.kind === 'type'
        ? `${subject} ${TYPE_PREDICATE} ${qqlTerm(o.cls)} .`
        : o.kind === 'link'
          ? `${subject} ${qqlTerm(o.property)} ${qqlTerm(o.term)} .`
          : `${subject} ${qqlTerm(o.property)} ${literal(o.valueKind, o.value)} .`;
  return { when: lines.join('\n'), then };
}

// ---- reading QQL back ----

type Tok =
  | { t: 'var'; v: string }
  | { t: 'term'; v: string }
  | { t: 'num'; v: string }
  | { t: 'str'; v: string }
  | { t: 'bool'; v: boolean }
  | { t: 'kw'; v: string }
  | { t: 'p'; v: string };

const KEYWORDS = new Set(['FILTER', 'NOT', 'EXISTS', 'IN', 'CONTAINS', 'LCASE']);

/** The tokens of the rule text the builder writes, or null for anything else. */
function tokenize(src: string): Tok[] | null {
  const out: Tok[] = [];
  let i = 0;
  const nameChar = /[\p{L}\p{N}_-]/u;
  while (i < src.length) {
    const ch = src.charAt(i);
    if (/\s/.test(ch)) {
      i++;
      continue;
    }
    if (ch === '?') {
      let j = i + 1;
      while (j < src.length && nameChar.test(src.charAt(j))) j++;
      if (j === i + 1) return null;
      out.push({ t: 'var', v: src.slice(i + 1, j) });
      i = j;
      continue;
    }
    if (ch === '<') {
      const next = src.charAt(i + 1);
      if (next === '=') {
        out.push({ t: 'p', v: '<=' });
        i += 2;
        continue;
      }
      if (next === '' || /\s/.test(next) || next === '?') {
        out.push({ t: 'p', v: '<' });
        i++;
        continue;
      }
      const end = src.indexOf('>', i);
      if (end < 0) return null;
      out.push({ t: 'term', v: src.slice(i + 1, end) });
      i = end + 1;
      continue;
    }
    if (ch === '>' || ch === '!' || ch === '=') {
      if (src.charAt(i + 1) === '=') {
        out.push({ t: 'p', v: `${ch}=` });
        i += 2;
      } else if (ch === '!') {
        return null;
      } else {
        out.push({ t: 'p', v: ch });
        i++;
      }
      continue;
    }
    if ('.,(){}'.includes(ch)) {
      if (ch === '.' && /\d/.test(src.charAt(i + 1))) return null;
      out.push({ t: 'p', v: ch });
      i++;
      continue;
    }
    if (ch === '"') {
      let j = i + 1;
      let text = '';
      while (j < src.length && src.charAt(j) !== '"') {
        if (src.charAt(j) === '\\') {
          j++;
          if (j >= src.length) return null;
        }
        text += src.charAt(j);
        j++;
      }
      if (j >= src.length) return null;
      out.push({ t: 'str', v: text });
      i = j + 1;
      continue;
    }
    const num = /^-?\d+(\.\d+)?/.exec(src.slice(i));
    if (num && (ch !== '-' || /\d/.test(src.charAt(i + 1)))) {
      out.push({ t: 'num', v: num[0] });
      i += num[0].length;
      continue;
    }
    if (ch === ':' || nameChar.test(ch)) {
      let j = i;
      while (j < src.length && nameChar.test(src.charAt(j))) j++;
      const word = src.slice(i, j);
      if (src.charAt(j) === ':') {
        let k = j + 1;
        while (k < src.length && nameChar.test(src.charAt(k))) k++;
        if (k === j + 1) return null;
        out.push({ t: 'term', v: src.slice(i, k) });
        i = k;
        continue;
      }
      if (word === 'true' || word === 'false') out.push({ t: 'bool', v: word === 'true' });
      else if (KEYWORDS.has(word.toUpperCase())) out.push({ t: 'kw', v: word.toUpperCase() });
      else return null;
      i = j;
      continue;
    }
    return null;
  }
  return out;
}

/** A dot before the end, a closing brace or a FILTER is an optional separator. */
function normalize(toks: Tok[]): Tok[] {
  return toks.filter((tok, i) => {
    if (tok.t !== 'p' || tok.v !== '.') return true;
    const next = toks[i + 1];
    return !(next === undefined || (next.t === 'p' && next.v === '}') || (next.t === 'kw' && next.v === 'FILTER'));
  });
}

/** Same tokens, with variables equal up to a consistent renaming. */
function equivalent(a: Tok[], b: Tok[]): boolean {
  if (a.length !== b.length) return false;
  const ab = new Map<string, string>();
  const ba = new Map<string, string>();
  return a.every((x, i) => {
    const y = b[i];
    if (!y || x.t !== y.t) return false;
    if (x.t === 'var' && y.t === 'var') {
      if ((ab.get(x.v) ?? y.v) !== y.v || (ba.get(y.v) ?? x.v) !== x.v) return false;
      ab.set(x.v, y.v);
      ba.set(y.v, x.v);
      return true;
    }
    return x.v === y.v;
  });
}

type Node = { t: 'var'; v: string } | { t: 'term'; v: string } | { t: 'lit'; tok: Tok };
type Item =
  | { kind: 'triple'; s: Node; p: string; o: Node }
  | { kind: 'filter'; toks: Tok[] }
  | { kind: 'notExists'; items: Item[] };

function node(tok: Tok | undefined): Node | null {
  if (!tok) return null;
  if (tok.t === 'var' || tok.t === 'term') return tok;
  if (tok.t === 'num' || tok.t === 'str' || tok.t === 'bool') return { t: 'lit', tok };
  return null;
}

function items(toks: Tok[]): Item[] | null {
  const out: Item[] = [];
  let i = 0;
  while (i < toks.length) {
    const tok = toks[i];
    if (tok?.t === 'kw' && tok.v === 'FILTER') {
      const a = toks[i + 1];
      const b = toks[i + 2];
      if (a?.t === 'kw' && a.v === 'NOT' && b?.t === 'kw' && b.v === 'EXISTS') {
        if (toks[i + 3]?.v !== '{') return null;
        let depth = 0;
        let j = i + 3;
        for (; j < toks.length; j++) {
          const v = toks[j]?.v;
          if (v === '{') depth++;
          if (v === '}' && --depth === 0) break;
        }
        if (j >= toks.length) return null;
        const inner = items(toks.slice(i + 4, j));
        if (!inner) return null;
        out.push({ kind: 'notExists', items: inner });
        i = j + 1;
        continue;
      }
      if (a?.v !== '(') return null;
      let depth = 0;
      let j = i + 1;
      for (; j < toks.length; j++) {
        const v = toks[j]?.v;
        if (v === '(') depth++;
        if (v === ')' && --depth === 0) break;
      }
      if (j >= toks.length) return null;
      out.push({ kind: 'filter', toks: toks.slice(i + 2, j) });
      i = j + 1;
      continue;
    }
    const s = node(toks[i]);
    const p = toks[i + 1];
    const o = node(toks[i + 2]);
    if (!s || s.t === 'lit' || p?.t !== 'term' || !o) return null;
    out.push({ kind: 'triple', s, p: p.v, o });
    i += 3;
    if (toks[i]?.v === '.') i++;
  }
  return out;
}

function sameVar(n: Node, name: string): boolean {
  return n.t === 'var' && n.v === name;
}

function literalValue(tok: Tok): { kind: LiteralKind; value: string } | null {
  if (tok.t === 'num') return { kind: 'number', value: tok.v };
  if (tok.t === 'str') return { kind: DATE.test(tok.v) ? 'date' : 'text', value: tok.v };
  return null;
}

/** One condition from `list` at `at`: the items it used and what it asks. */
function readCondition(
  list: Item[],
  at: number,
  owners: Map<string, 'subject' | 'linked'>,
): { condition: Condition; used: number } | null {
  const first = list[at];
  if (first?.kind !== 'triple' || first.s.t !== 'var') return null;
  const on = owners.get(first.s.v);
  if (!on) return null;
  const property = first.p;
  const o = first.o;
  if (o.t === 'term') return { condition: { on, property, test: { kind: 'is', term: o.v } }, used: 1 };
  if (o.t === 'lit') {
    return o.tok.t === 'bool'
      ? { condition: { on, property, test: { kind: 'flag', value: o.tok.v } }, used: 1 }
      : null;
  }
  const v = o.v;
  const next = list[at + 1];
  if (next?.kind === 'filter') {
    const f = next.toks;
    if (f.length === 3 && f[0]?.t === 'var' && f[0].v === v && f[1]?.t === 'p') {
      const op = SYMBOL_OP[f[1].v];
      const lit = f[2] ? literalValue(f[2]) : null;
      if (op && lit) {
        return {
          condition: { on, property, test: { kind: 'compare', op, valueKind: lit.kind, value: lit.value } },
          used: 2,
        };
      }
    }
    if (f[0]?.t === 'var' && f[0].v === v && f[1]?.v === 'IN' && f[2]?.v === '(' && f.at(-1)?.v === ')') {
      const inner = f.slice(3, -1).filter((_, i) => i % 2 === 0);
      const commas = f.slice(3, -1).filter((_, i) => i % 2 === 1);
      if (inner.length > 0 && commas.every((c) => c.v === ',')) {
        if (inner.every((x) => x.t === 'term')) {
          return {
            condition: { on, property, test: { kind: 'anyOf', valueKind: 'thing', values: inner.map((x) => String(x.v)) } },
            used: 2,
          };
        }
        if (inner.every((x) => x.t === 'num') || inner.every((x) => x.t === 'str')) {
          return {
            condition: {
              on,
              property,
              test: { kind: 'anyOf', valueKind: inner[0]?.t === 'num' ? 'number' : 'text', values: inner.map((x) => String(x.v)) },
            },
            used: 2,
          };
        }
      }
    }
    const shape = f.map((x) => (x.t === 'var' ? `?${x.v}` : x.t === 'str' ? '"' : String(x.v))).join(' ');
    if (shape === `CONTAINS ( LCASE ( ?${v} ) , " )`) {
      const text = f[7];
      if (text?.t === 'str') return { condition: { on, property, test: { kind: 'contains', text: text.v } }, used: 2 };
    }
  }
  const after = list[at + 2];
  if (
    next?.kind === 'triple' &&
    next.s.t === 'var' &&
    next.s.v === first.s.v &&
    next.o.t === 'var' &&
    after?.kind === 'filter' &&
    after.toks.length === 3 &&
    after.toks[0]?.t === 'var' &&
    after.toks[0].v === v &&
    after.toks[2]?.t === 'var' &&
    after.toks[2].v === next.o.v
  ) {
    const op = after.toks[1] ? SYMBOL_OP[String(after.toks[1].v)] : undefined;
    if (op) {
      return { condition: { on, property, test: { kind: 'compareProperty', op, property: next.p } }, used: 3 };
    }
  }
  return { condition: { on, property, test: { kind: 'exists' } }, used: 1 };
}

function usesAsSubject(list: Item[], name: string): boolean {
  return list.some((it) =>
    it.kind === 'triple' ? sameVar(it.s, name) : it.kind === 'notExists' ? usesAsSubject(it.items, name) : false,
  );
}

function readOutcome(then: Tok[], subject: string): Outcome | null {
  if (then.length === 0) return { kind: 'select' };
  const list = items(then);
  const only = list?.[0];
  if (!list || list.length !== 1 || only?.kind !== 'triple' || !sameVar(only.s, subject)) return null;
  if (only.o.t === 'term') {
    return only.p === TYPE_PREDICATE
      ? { kind: 'type', cls: only.o.v }
      : { kind: 'link', property: only.p, term: only.o.v };
  }
  if (only.o.t !== 'lit') return null;
  const tok = only.o.tok;
  if (tok.t === 'bool') return { kind: 'value', property: only.p, valueKind: 'boolean', value: String(tok.v) };
  if (tok.t === 'num') return { kind: 'value', property: only.p, valueKind: 'number', value: tok.v };
  if (tok.t === 'str') return { kind: 'value', property: only.p, valueKind: 'text', value: tok.v };
  return null;
}

/** The form a stored rule's text stands for, or null when the text is not one
 *  of the shapes the builder writes. Exact: the result writes the same rule. */
export function parseRule(when: string, then: string): BuilderRule | null {
  const whenToks = tokenize(when);
  const thenToks = tokenize(then);
  if (!whenToks || !thenToks) return null;
  const list = items(whenToks);
  const head = list?.[0];
  if (!list || head?.kind !== 'triple' || head.s.t !== 'var' || head.p !== TYPE_PREDICATE || head.o.t !== 'term') {
    return null;
  }
  const subject = head.s.v;
  const owners = new Map<string, 'subject' | 'linked'>([[subject, 'subject']]);
  let at = 1;
  let link: BuilderRule['link'] = null;
  const second = list[1];
  if (
    second?.kind === 'triple' &&
    sameVar(second.s, subject) &&
    second.o.t === 'var' &&
    usesAsSubject(list.slice(2), second.o.v)
  ) {
    const linkedVar = second.o.v;
    const third = list[2];
    const cls = third?.kind === 'triple' && sameVar(third.s, linkedVar) && third.p === TYPE_PREDICATE && third.o.t === 'term' ? third.o.v : null;
    link = { property: second.p, cls };
    owners.set(linkedVar, 'linked');
    at = cls ? 3 : 2;
  }
  const conditions: Condition[] = [];
  const unless: Condition[] = [];
  while (at < list.length) {
    const it = list[at];
    if (it?.kind === 'notExists') {
      const read = readCondition(it.items, 0, owners);
      if (!read || read.used !== it.items.length) return null;
      unless.push(read.condition);
      at++;
      continue;
    }
    const read = readCondition(list, at, owners);
    if (!read) return null;
    conditions.push(read.condition);
    at += read.used;
  }
  const outcome = readOutcome(thenToks, subject);
  if (!outcome) return null;
  const rule: BuilderRule = { subjectClass: head.o.v, link, conditions, unless, outcome };
  if (builderProblems(rule).length > 0) return null;
  const written = generateRule(rule);
  const again = { when: tokenize(written.when), then: tokenize(written.then) };
  if (!again.when || !again.then) return null;
  return equivalent(normalize(whenToks), normalize(again.when)) && equivalent(normalize(thenToks), normalize(again.then))
    ? rule
    : null;
}

// ---- reading it aloud ----

const OP_WORDS: Record<LiteralKind, Record<CompareOp, string>> = {
  number: { eq: 'is', ne: 'is not', gt: 'is more than', ge: 'is at least', lt: 'is less than', le: 'is at most' },
  text: { eq: 'is', ne: 'is not', gt: 'comes after', ge: 'is or comes after', lt: 'comes before', le: 'is or comes before' },
  date: { eq: 'is on', ne: 'is not on', gt: 'is after', ge: 'is on or after', lt: 'is before', le: 'is on or before' },
};

export function opWords(kind: LiteralKind, op: CompareOp): string {
  return OP_WORDS[kind][op];
}

function list(values: string[]): string {
  if (values.length <= 1) return values.join('');
  return `${values.slice(0, -1).join(', ')} or ${values.at(-1) ?? ''}`;
}

function article(word: string): string {
  return /^[aeiou]/i.test(word) ? `an ${word}` : `a ${word}`;
}

/** The rule as one plain sentence. `name` gives a term's label. */
export function describeRule(rule: BuilderRule, name: (term: string) => string): string {
  const subject = rule.subjectClass ? name(rule.subjectClass) : 'thing';
  const linkedName = rule.link ? (rule.link.cls ? name(rule.link.cls) : name(rule.link.property)) : '';
  const whose = (c: Condition) => (c.on === 'linked' && rule.link ? `that ${linkedName}'s` : 'its');
  const phrase = (c: Condition): string => {
    const p = c.property ? name(c.property) : '…';
    const t = c.test;
    switch (t.kind) {
      case 'is':
        return `${whose(c)} ${p} is ${t.term ? name(t.term) : '…'}`;
      case 'anyOf':
        return `${whose(c)} ${p} is any of ${list(t.values.map((v) => (t.valueKind === 'thing' ? name(v) : t.valueKind === 'text' ? `“${v}”` : v))) || '…'}`;
      case 'flag':
        return `${whose(c)} ${p} is ${t.value ? 'true' : 'false'}`;
      case 'compare':
        return `${whose(c)} ${p} ${OP_WORDS[t.valueKind][t.op]} ${t.valueKind === 'text' ? `“${t.value}”` : t.value || '…'}`;
      case 'compareProperty':
        return `${whose(c)} ${p} ${OP_WORDS.number[t.op]} ${whose(c)} ${t.property ? name(t.property) : '…'}`;
      case 'contains':
        return `${whose(c)} ${p} contains “${t.text}”`;
      case 'exists':
        return c.on === 'linked' && rule.link ? `that ${linkedName} has ${article(p)}` : `it has ${article(p)}`;
    }
  };
  let thing = subject;
  if (rule.link) {
    const via = rule.link.property ? name(rule.link.property) : '…';
    thing += ` that has ${article(via)}${rule.link.cls ? ` that is ${article(name(rule.link.cls))}` : ''}`;
  }
  const conds = rule.conditions.map(phrase);
  const unless = rule.unless.map(phrase);
  let sentence = conds.length + unless.length === 0 ? `Every ${thing}:` : `For ${article(thing)}:`;
  if (conds.length > 0) sentence += ` when ${conds.join(', and ')}`;
  if (unless.length > 0) sentence += `${conds.length > 0 ? ',' : ''} unless ${unless.join(', or ')}`;
  const o = rule.outcome;
  const outcome =
    o.kind === 'select'
      ? 'the rule picks it out, and adds no fact'
      : o.kind === 'type'
        ? `it is ${article(o.cls ? name(o.cls) : '…')}`
        : o.kind === 'link'
          ? `it gets ${o.property ? name(o.property) : '…'} ${o.term ? name(o.term) : '…'}`
          : `its ${o.property ? name(o.property) : '…'} is ${o.valueKind === 'text' ? `“${o.value}”` : o.value || '…'}`;
  return conds.length + unless.length === 0 ? `${sentence} ${outcome}.` : `${sentence}, then ${outcome}.`;
}
